import { type FC, useState, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEcommerce, eventNameConstants, trackingConstants } from 'dibs-tracking';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import classnames from 'classnames';
import ArticleLink from './ArticleLink';

import { type EditorialModuleArticles_articles$data } from './__generated__/EditorialModuleArticles_articles.graphql';

import styles from './styles/EditorialModuleArticles.scss';

import { Button } from 'dibs-elements/exports/Button';

type Props = {
    articles: EditorialModuleArticles_articles$data;
    useWhiteBg?: boolean;
    headerClassName?: string;
    launchOrigin?: string;
};

// determine grid layout cols as described in GROWTH-6730
const getColsCount = (articles: EditorialModuleArticles_articles$data): number => {
    if (articles.length < 5) {
        return articles.length;
    }

    if (articles.length < 7) {
        return 3;
    }

    return 4;
};

const EditorialModuleArticles: FC<Props> = ({
    articles,
    useWhiteBg,
    headerClassName,
    launchOrigin,
}) => {
    const [expanded, setExpanded] = useState(false);
    const containerRef = useRef(null);

    const promotionId = `${launchOrigin} introspective`;
    const promotionName = 'introspective';

    // more articles gives us "stacked" article content layout
    const stacked = articles.length > 2;

    // put articles into a css grid
    const colsCount = getColsCount(articles);
    const articlesGrid = (
        <div
            ref={containerRef}
            className={classnames(styles.articlesWrapper, {
                [styles.col1]: colsCount === 1,
                [styles.col2]: colsCount === 2,
                [styles.col3]: colsCount === 3,
                [styles.col4]: colsCount === 4,
            })}
        >
            <VisibilityTracker
                elementRef={containerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackEcommerce({
                            type: trackingConstants.ECOM_PRODUCT_PROMO_IMPRESSION,
                            eventName: eventNameConstants.EVENT_VIEW_PROMOTION,
                            promotions: articles.map((article, index) => ({
                                id: promotionId,
                                name: promotionName,
                                creative: article.title,
                                position: index + 1,
                            })),
                        });
                    }
                }}
            />
            {articles.map((article, index) => {
                // hide article if not "view more" and not first row
                const firstRow = index + 1 <= colsCount;
                const hidden = !expanded && !firstRow;

                return (
                    <ArticleLink
                        key={index}
                        article={article}
                        index={index}
                        stacked={stacked}
                        hidden={hidden}
                        onClick={() => {
                            trackEcommerce({
                                type: trackingConstants.ECOM_PRODUCT_PROMO_CLICK,
                                eventName: eventNameConstants.EVENT_SELECT_PROMOTION,
                                promotions: [
                                    {
                                        id: promotionId,
                                        name: promotionName,
                                        creative: article.title,
                                        position: index + 1,
                                    },
                                ],
                            });
                        }}
                    />
                );
            })}
        </div>
    );

    return (
        <HeadingLevel>
            {Heading => (
                <div
                    className={classnames(styles.container, { [styles.useWhiteBg]: !!useWhiteBg })}
                >
                    <Heading className={`${styles.sectionHeader} ${headerClassName}`}>
                        <FormattedMessage
                            defaultMessage="Read More"
                            id="dbl.editorialModule.header"
                        />
                    </Heading>

                    {articlesGrid}

                    {articles.length > colsCount && (
                        <Button
                            type="secondary"
                            size="medium"
                            onClick={() => {
                                setExpanded(!expanded);
                            }}
                            className={styles.viewMoreButton}
                        >
                            {expanded ? (
                                <FormattedMessage
                                    id="dbl.editorialModule.viewLess"
                                    defaultMessage="View Less"
                                />
                            ) : (
                                <FormattedMessage
                                    id="dbl.editorialModule.viewMore"
                                    defaultMessage="View More"
                                />
                            )}
                        </Button>
                    )}
                </div>
            )}
        </HeadingLevel>
    );
};

export default createFragmentContainer(EditorialModuleArticles, {
    articles: graphql`
        fragment EditorialModuleArticles_articles on WordpressArticleType @relay(plural: true) {
            publishedDate
            title
            ...ArticleLink_article
        }
    `,
});
